import { BadgeCheckInModal } from '@/ui/features/missions/components/BadgeCheckInModal';
import { useCheckInModalData } from '@/ui/hooks/useCheckInModalData';
import { showCustomDialog } from '@juvare/common-web-ui/DialogHelper';
import { useFeatureFlag } from '@juvare/common-web-ui/hooks';
import { useCheckInCheckoutActions } from '@juvare/missions/ui/features/ongoingShifts/hooks/useCheckInCheckoutActions';
import ClearIcon from '@mui/icons-material/Clear';
import { Box, Button, IconButton, InputAdornment, TextField } from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

const badgeNumberParamsKey = 'badge';

export const FindVolunteer: React.FC<{ onSuccess?: () => void; shortButtonLabel?: boolean; missionId?: string; shiftId?: string }> = ({
    shortButtonLabel,
    onSuccess,
    missionId,
    shiftId,
}) => {
    const findVolunteerByBadgeFeature = useFeatureFlag('rms', 'findVolunteerByBadge');

    const [searchParams, setSearchParams] = useSearchParams();
    const [accountBadgeNumber, setAccountBadgeNumber] = React.useState<string | undefined>(
        searchParams.get(badgeNumberParamsKey) || undefined,
    );
    const [query, setQuery] = React.useState<string>(searchParams.get(badgeNumberParamsKey) || '');
    const [openModal, setOpenModal] = React.useState(false);

    const [openModalTimeout, setOpenModalTimeout] = React.useState<ReturnType<typeof setTimeout>>();

    const navigate = useNavigate();

    const { t } = useTranslation('rms');

    const { handleCheckInVolunteer, handleCheckOutVolunteer } = useCheckInCheckoutActions();

    const { data, error, fetchData } = useCheckInModalData({ accountBadgeNumber, missionId, shiftId });

    useEffect(() => {
        const accountBadgeNo = searchParams.get(badgeNumberParamsKey);

        setAccountBadgeNumber(accountBadgeNo || undefined);
    }, [searchParams]);

    useEffect(() => {
        if (searchParams.get(badgeNumberParamsKey)) {
            setOpenModal(true);
        }
    }, []);

    const handleOpenModal = () => {
        clearTimeout(openModalTimeout);

        const indexOfDAQ = query.indexOf('DAQ');
        const indexOfDAYPartial = query.slice(indexOfDAQ, query.length).indexOf('DAY');
        const indexOfDAY = indexOfDAYPartial > 0 ? indexOfDAYPartial + indexOfDAQ : indexOfDAYPartial;

        let accountBadgeNumber = query.trim();

        if (indexOfDAQ > 0 && indexOfDAY > 0) {
            accountBadgeNumber = query.slice(indexOfDAQ + 3, indexOfDAY);
        }

        const newSearchParams = new URLSearchParams(searchParams);

        if (accountBadgeNumber) {
            newSearchParams.set(badgeNumberParamsKey, accountBadgeNumber);
        } else {
            newSearchParams.delete(badgeNumberParamsKey);
        }

        setSearchParams(newSearchParams);
        setAccountBadgeNumber(accountBadgeNumber);

        setOpenModalTimeout(
            setTimeout(() => {
                setOpenModal(true);
                setOpenModalTimeout(undefined);
            }, 250),
        );
    };

    const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(event.target.value);

        if (openModalTimeout && !openModal) {
            handleOpenModal();
        }
    };
    const handleQueryClear = () => {
        setQuery('');

        setAccountBadgeNumber(undefined);

        const newSearchParams = new URLSearchParams(searchParams);

        newSearchParams.delete(badgeNumberParamsKey);

        setSearchParams(newSearchParams);
    };

    useEffect(() => {
        if (accountBadgeNumber && (data || error) && openModal && findVolunteerByBadgeFeature.enabled) {
            showCustomDialog({
                maxWidth: error ? 'sm' : 'md',
                content: (close) => (
                    <BadgeCheckInModal
                        handleCheckInVolunteer={handleCheckInVolunteer}
                        handleCheckOutVolunteer={handleCheckOutVolunteer}
                        data={data}
                        error={error}
                        reloadData={fetchData}
                        close={close}
                        onSuccess={() => onSuccess?.()}
                        onCloseModal={() => {
                            close();

                            setQuery('');
                            navigate('/');
                        }}
                    />
                ),
            });

            setOpenModal(false);
        }
    }, [accountBadgeNumber, data, error, searchParams, openModal]);

    if (!findVolunteerByBadgeFeature.enabled) {
        return null;
    }

    return (
        <Box
            sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'end',
                minHeight: 40,
                maxWidth: 400,
            }}
            data-test='toolbar'
            component='form'
            onSubmit={async (event) => {
                event.preventDefault();
                event.stopPropagation();

                handleOpenModal();
            }}
        >
            <TextField
                sx={{ width: '100%', marginRight: 2 }}
                label={t('pages.home.missions.scanOrEnterBadgeCode')}
                size='small'
                value={query}
                onChange={handleQueryChange}
                onKeyDown={(event) => {
                    if (event.ctrlKey) {
                        event.stopPropagation();
                        event.preventDefault();

                        return;
                    }
                }}
                fullWidth={true}
                slotProps={{
                    input: {
                        endAdornment:
                            query.trim().length > 0 ? (
                                <InputAdornment position='end'>
                                    <IconButton
                                        size='medium'
                                        edge='end'
                                        onClick={handleQueryClear}
                                        onMouseDown={(event) => event.preventDefault()}
                                    >
                                        <ClearIcon />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                    },
                }}
            />
            <Button type='submit' variant='contained' color='primary' sx={{ minHeight: 40, minWidth: 'fit-content' }}>
                {shortButtonLabel ? t('pages.home.missions.find') : t('pages.home.missions.findVolunteer')}
            </Button>
        </Box>
    );
};
